import React from "react";
import { t } from "ttag";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { ButtonPrimary, ButtonSecondary } from "../../Clickables";
import { ContributionCard } from "../ContributionCard/ContributionCard";
import { ModalFooter } from "../ModalFooter/ModalFooter";
import { ResponsiveLabel } from "../ResponsiveLabel/ResponsiveLabel";
import {
    Action,
    NavigationTarget,
    ProductCategory,
    WalkAppState,
    getConfirmation,
    getContinueTarget,
    skuCatalog,
} from "../engine";

import styles from "./Confirmation.module.scss";

/**
 * Maps a `getContinueTarget` result to the Continue button's
 * user-facing label.
 */
const continueLabelFor = (target: NavigationTarget): string => {
    switch (target.kind) {
        case "next-chain-entry":
            return target.targetLabel
                ? t`Next: ${target.targetLabel}`
                : t`Next`;
        case "next-spoke":
            return t`Next: ${target.targetLabel}`;
        case "complete":
            return t`Complete My Suite`;
    }
    return assertNever(target);
};

const headingFor = (category: ProductCategory): string => {
    switch (category) {
        case "range":
            return t`This range has been added to your suite`;
        case "cooktop":
            return t`This cooktop has been added to your suite`;
        case "range_top":
            return t`This rangetop has been added to your suite`;
        case "wall_oven":
            return t`This wall oven has been added to your suite`;
        case "speed_oven":
            return t`This speed oven has been added to your suite`;
        case "microwave":
            return t`This microwave has been added to your suite`;
        case "hood":
            return t`This hood has been added to your suite`;
        case "dishwasher":
            return t`This dishwasher has been added to your suite`;
        case "refrigerator":
            return t`This refrigerator has been added to your suite`;
        case "freezer":
            return t`This freezer has been added to your suite`;
        case "wine_refrigerator":
            return t`This wine refrigerator has been added to your suite`;
    }
    return assertNever(category);
};

export type ConfirmationProps = {
    state: WalkAppState;
    dispatch: React.Dispatch<Action>;
    onShowOfferDetails: () => void;
};

export const Confirmation = ({
    state,
    dispatch,
    onShowOfferDetails,
}: ConfirmationProps) => {
    const walk = state.walk;
    const vm = getConfirmation(state);
    if (!vm) return null;

    const category = skuCatalog[vm.contribution.mainSku].category;
    const headingText = category
        ? headingFor(category)
        : t`Added to your suite`;

    return (
        <section className={styles.confirmation}>
            <div className={styles.body}>
                <div className={styles.grid}>
                    <div className={styles.inner}>
                        <h2 className={styles.heading}>{headingText}</h2>
                        <ContributionCard
                            contribution={vm.contribution}
                            hasJoiningKit={vm.hasJoiningKit}
                            mode="confirmation"
                        />
                    </div>
                </div>
            </div>
            <ModalFooter
                state={state}
                onShowOfferDetails={onShowOfferDetails}
                left={
                    <ButtonPrimary onClick={() => dispatch({ type: "BACK" })}>
                        {t`Back`}
                    </ButtonPrimary>
                }
                right={
                    <ButtonSecondary
                        onClick={() =>
                            dispatch({ type: "CONTINUE_FROM_CONFIRMATION" })
                        }
                    >
                        <ResponsiveLabel
                            full={continueLabelFor(getContinueTarget(walk))}
                            short={t`Next`}
                        />
                    </ButtonSecondary>
                }
            />
        </section>
    );
};
